import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, CreditCard, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useSite } from "../contexts/SiteContext";
import { useLocale } from "../contexts/LocaleContext";
import api, { formatErr } from "../lib/api";

const STEPS = ["Contact", "Shipping", "Payment", "Review"];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { settings } = useSite();
  const { fmt } = useLocale();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || "", customer_email: user?.email || "", customer_phone: user?.phone || "",
    shipping_address: "", shipping_city: "", shipping_country: "United Arab Emirates", shipping_zip: "",
    payment_method: "stripe", discount_code: "", notes: "",
  });
  const upd = (k,v) => setForm({...form, [k]: v});
  const isUAE = (form.shipping_country||"").toLowerCase().includes("uae") || form.shipping_country.toLowerCase().includes("emirates");
  const shipping = subtotal >= (settings.free_shipping_threshold_aed||5000) ? 0 : (isUAE ? settings.shipping_uae_aed : settings.shipping_intl_aed) || 0;
  const total = subtotal + shipping;
  const paymentsLocked = !!settings.payments_disabled;
  const paymentsLockedMsg = settings.payments_disabled_message || "Online payments are temporarily unavailable.";

  if (items.length === 0) return <div className="py-32 text-center text-gray-500">Your cart is empty. <a className="text-copper" href="/shop">Continue shopping</a></div>;

  const next = () => {
    if (step === 0 && (!form.customer_name || !form.customer_email || !form.customer_phone)) return toast.error("Please fill all contact details");
    if (step === 1 && (!form.shipping_address || !form.shipping_city || !form.shipping_country)) return toast.error("Please complete shipping address");
    setStep(s=>Math.min(3, s+1));
  };
  const submit = async () => {
    if (paymentsLocked) { toast.error(paymentsLockedMsg); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        ...form,
        origin_url: window.location.origin,
      };
      const r = await api.post("/checkout", payload);
      if (r.data.provider === "stripe" && r.data.checkout_url) {
        // Stripe live: stash order id for confirmation page polling, then redirect
        sessionStorage.setItem("pending_order_id", r.data.order_id);
        clear();
        window.location.href = r.data.checkout_url;
        return;
      }
      // COD / non-stripe → direct confirmation
      clear();
      toast.success("Order placed");
      nav(`/order/${r.data.order_id}`);
    } catch (e) { toast.error(formatErr(e)); } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-[1300px] mx-auto px-6 md:px-12 py-14">
      <div className="overline mb-2">Checkout</div>
      <h1 className="font-display text-4xl text-dark mb-4">Complete Your Order</h1>
      {paymentsLocked && (
        <div data-testid="payments-lock-banner" className="mb-6 border border-copper/40 bg-copper/5 px-4 py-3 flex gap-3 items-start">
          <Lock size={16} className="text-copper mt-0.5 shrink-0"/>
          <div>
            <div className="text-sm text-dark font-semibold">Payments are temporarily disabled</div>
            <div className="text-xs text-gray-600 mt-0.5">{paymentsLockedMsg}</div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-12 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${i<step? "bg-copper text-white" : i===step? "bg-dark text-white" : "bg-[#FAF7F2] text-gray-400 border border-black/10"}`}>
              {i<step ? <Check size={13}/> : i+1}
            </div>
            <div className={`ml-2 mr-4 text-xs uppercase tracking-luxe ${i===step? "text-dark" : "text-gray-400"}`}>{s}</div>
            {i<STEPS.length-1 && <ChevronRight size={14} className="text-gray-300 mr-3"/>}
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-[1fr_380px] gap-10">
        <div className="card-luxe p-8">
          {step === 0 && (
            <div className="space-y-5">
              <h3 className="font-display text-xl text-dark mb-4">Contact</h3>
              <input data-testid="ck-name" placeholder="Full Name" className="input-box" value={form.customer_name} onChange={(e)=>upd("customer_name",e.target.value)}/>
              <input data-testid="ck-email" type="email" placeholder="Email" className="input-box" value={form.customer_email} onChange={(e)=>upd("customer_email",e.target.value)}/>
              <input data-testid="ck-phone" placeholder="Phone" className="input-box" value={form.customer_phone} onChange={(e)=>upd("customer_phone",e.target.value)}/>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-5">
              <h3 className="font-display text-xl text-dark mb-4">Shipping Address</h3>
              <input data-testid="ck-address" placeholder="Street Address" className="input-box" value={form.shipping_address} onChange={(e)=>upd("shipping_address",e.target.value)}/>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="ck-city" placeholder="City" className="input-box" value={form.shipping_city} onChange={(e)=>upd("shipping_city",e.target.value)}/>
                <input data-testid="ck-zip" placeholder="ZIP / Postal" className="input-box" value={form.shipping_zip} onChange={(e)=>upd("shipping_zip",e.target.value)}/>
              </div>
              <input data-testid="ck-country" placeholder="Country" className="input-box" value={form.shipping_country} onChange={(e)=>upd("shipping_country",e.target.value)}/>
              <textarea data-testid="ck-notes" placeholder="Order notes (optional)" rows={3} className="input-box resize-none" value={form.notes} onChange={(e)=>upd("notes",e.target.value)}/>
              <div className="text-xs text-gray-500 pt-2 border-t border-black/5">Shipping: <span className="text-copper font-semibold">{fmt(shipping)}</span> via DHL{shipping===0 && <span className="ml-2 text-green-600">FREE shipping unlocked</span>}</div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5 relative" data-testid="checkout-payment-step">
              <h3 className="font-display text-xl text-dark mb-4">Payment Method</h3>
              <div className={paymentsLocked ? "pointer-events-none opacity-40 select-none" : ""} aria-disabled={paymentsLocked} data-testid="payment-options">
                {[
                  { id: "stripe", l: "Credit / Debit Card (Stripe)", d: "Encrypted, secure card processing" },
                  { id: "paypal", l: "PayPal", d: "Pay with your PayPal account" },
                  { id: "cod", l: "Bank Transfer / On Delivery", d: "Our concierge will be in touch" },
                ].map(o => (
                  <button key={o.id} data-testid={`pay-${o.id}`} type="button" disabled={paymentsLocked} onClick={() => upd("payment_method", o.id)} className={`w-full text-left p-4 border mb-3 ${form.payment_method === o.id ? "border-copper bg-[#FAF7F2]" : "border-black/10 hover:border-black/30"}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${form.payment_method === o.id ? "border-copper bg-copper" : "border-gray-400"}`}/>
                      <div><div className="text-dark text-sm">{o.l}</div><div className="text-xs text-gray-500">{o.d}</div></div>
                    </div>
                  </button>
                ))}
                <div><div className="overline mb-2">Discount Code</div><input data-testid="ck-discount" placeholder="Enter code" disabled={paymentsLocked} className="input-box" value={form.discount_code} onChange={(e) => upd("discount_code", e.target.value)}/></div>
                <p className="text-[11px] text-gray-500 italic mt-3">{form.payment_method === "stripe" ? "Secure card payments processed by Stripe. You'll be redirected to Stripe's hosted checkout." : form.payment_method === "cod" ? "Pay on delivery — our concierge will contact you to confirm." : "You&apos;ll be redirected to PayPal to complete payment."}</p>
              </div>
              {paymentsLocked && (
                <div data-testid="payment-locked-overlay" className="absolute inset-0 -m-4 flex items-center justify-center">
                  <div className="bg-white border border-copper/40 shadow-xl px-7 py-6 max-w-md text-center">
                    <Lock size={28} className="mx-auto text-copper mb-3"/>
                    <div className="font-display text-xl text-dark mb-2">Payments Temporarily Unavailable</div>
                    <p className="text-sm text-gray-600 leading-relaxed">{paymentsLockedMsg}</p>
                    <p className="text-[11px] uppercase tracking-luxe text-copper mt-4">Your details are safe — feel free to fill them in. We&apos;ll reach out to confirm.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-display text-xl text-dark mb-4">Review Order</h3>
              <div className="text-sm space-y-2 text-gray-700">
                <div><span className="text-gray-400">Name:</span> {form.customer_name}</div>
                <div><span className="text-gray-400">Email:</span> {form.customer_email}</div>
                <div><span className="text-gray-400">Phone:</span> {form.customer_phone}</div>
                <div><span className="text-gray-400">Ship to:</span> {form.shipping_address}, {form.shipping_city}, {form.shipping_country} {form.shipping_zip}</div>
                <div><span className="text-gray-400">Payment:</span> {form.payment_method}</div>
              </div>
              <div className="border-t border-black/5 pt-4">
                {items.map(i => <div key={i.product_id} className="flex justify-between text-sm py-1"><span>{i.name} ×{i.quantity}</span><span>{fmt(i.price*i.quantity)}</span></div>)}
              </div>
            </div>
          )}
          <div className="flex justify-between mt-8 pt-6 border-t border-black/5">
            <button data-testid="ck-back" disabled={step===0} onClick={()=>setStep(s=>Math.max(0,s-1))} className="btn-ghost text-xs uppercase tracking-luxe flex items-center gap-1 disabled:opacity-30"><ArrowLeft size={13}/> Back</button>
            {step < 3 ? <button data-testid="ck-next" onClick={next} className="btn-primary">Continue <ChevronRight size={14}/></button>
              : <button data-testid="ck-place-order" onClick={submit} disabled={submitting || paymentsLocked} title={paymentsLocked ? paymentsLockedMsg : ""} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                  {paymentsLocked ? <><Lock size={14}/> Payments Locked</> : <><CreditCard size={14}/> {submitting? "Processing…" : "Place Order"}</>}
                </button>}
          </div>
        </div>
        <aside className="card-luxe p-6 h-fit">
          <div className="overline mb-4">Order Summary</div>
          <div className="space-y-3 mb-4">
            {items.map(i => (
              <div key={i.product_id} className="flex gap-3">
                <img src={i.image} alt="" className="w-14 h-14 object-cover"/>
                <div className="flex-1 text-sm"><div className="text-dark">{i.name}</div><div className="text-gray-400 text-xs">×{i.quantity}</div></div>
                <div className="text-sm">{fmt(i.price*i.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-black/5 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>{fmt(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Shipping</span><span>{fmt(shipping)}</span></div>
            <div className="flex justify-between font-display text-lg pt-3 border-t border-black/5"><span>Total</span><span className="text-copper">{fmt(total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
